import React, { useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  LineElement,
  PointElement,
  RadialLinearScale,
} from 'chart.js';
import { Bar, Pie, Line, Doughnut, PolarArea, Radar } from 'react-chartjs-2';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Palette, BarChart, PieChart, LineChart, Radar as RadarIcon, Settings } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { 
  Tooltip as UITooltip, 
  TooltipContent, 
  TooltipProvider, 
  TooltipTrigger 
} from "@/components/ui/tooltip";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  LineElement,
  PointElement,
  RadialLinearScale,
);

export interface ChartData {
  name: string;
  value: number;
  fullName?: string; // Para tooltips con el nombre completo
}

interface DynamicChartProps {
  title: string;
  description?: string;
  data: ChartData[];
  defaultType?: ChartType;
  isRatingChart?: boolean;
  averageValue?: number;
}

type ChartType = 'bar' | 'pie' | 'line' | 'doughnut' | 'polarArea' | 'radar' | 'table' | 'horizontalBar';

const colorPalettes = {
  default: ['#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'],
  ocean: ['#0EA5E9', '#0284C7', '#0369A1', '#075985', '#0C4A6E', '#164E63', '#155E75', '#0891B2'],
  nature: ['#10B981', '#059669', '#047857', '#065F46', '#064E3B', '#6EE7B7', '#34D399', '#A7F3D0'],
  sunset: ['#F97316', '#EA580C', '#DC2626', '#B91C1C', '#991B1B', '#FED7AA', '#FDBA74', '#FB923C'],
  purple: ['#8B5CF6', '#7C3AED', '#6D28D9', '#5B21B6', '#4C1D95', '#C4B5FD', '#A78BFA', '#8B5CF6'],
  monochrome: ['#374151', '#4B5563', '#6B7280', '#9CA3AF', '#D1D5DB', '#E5E7EB', '#F3F4F6', '#F9FAFB']
};

const chartTypeIcons = {
  bar: BarChart,
  pie: PieChart,
  line: LineChart,
  doughnut: PieChart,
  polarArea: PieChart,
  radar: RadarIcon,
  table: Settings,
  horizontalBar: BarChart,
};

export const DynamicChart: React.FC<DynamicChartProps> = ({
  title,
  description,
  data,
  defaultType = 'bar',
  isRatingChart = false,
  averageValue
}) => {
  const [chartType, setChartType] = useState<ChartType>(defaultType);
  const [colorPalette, setColorPalette] = useState<keyof typeof colorPalettes>('default');
  const [showControls, setShowControls] = useState(false);

  const generateChartData = () => {
    const colors = colorPalettes[colorPalette];
    const labels = data.map(item => item.name);
    const values = data.map(item => item.value);

    const baseConfig = {
      labels,
      datasets: [
        {
          label: '', // Quitar etiqueta de la leyenda
          data: values,
          backgroundColor: colors.slice(0, data.length),
          borderColor: colors.slice(0, data.length),
          borderWidth: 1,
        },
      ],
    };

    return baseConfig;
  };

  const renderTable = () => {
    const sortedData = [...data].sort((a, b) => b.value - a.value);
    
    return (
      <div className="overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Categoría</TableHead>
              <TableHead className="text-right">Respuestas</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedData.map((item, index) => (
              <TableRow key={index}>
                <TableCell className="font-medium">
                  {item.fullName ? (
                    <TooltipProvider>
                      <UITooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-help">{item.name}</span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p>{item.fullName}</p>
                        </TooltipContent>
                      </UITooltip>
                    </TooltipProvider>
                  ) : (
                    item.name
                  )}
                </TableCell>
                <TableCell className="text-right">{item.value}</TableCell>   
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  const renderHorizontalBar = () => {
    const chartData = generateChartData();
    const horizontalOptions = {
      ...chartOptions,
      indexAxis: 'y' as const,
      scales: {
        x: {
          beginAtZero: true,
          ticks: isRatingChart ? {
            stepSize: 1,
            precision: 0
          } : undefined,
          title: {
            display: true,
            text: isRatingChart ? 'Nº de respuestas' : 'Cantidad'
          }
        },
        y: isRatingChart ? {
          title: {
            display: true,
            text: 'Calificación (1-10)'
          }
        } : undefined,
      },
      plugins: {
        ...chartOptions.plugins,
        legend: {
          display: false, // Ocultar leyenda para barras horizontales
        },
      },
    };

    return <Bar data={chartData} options={horizontalOptions} />;
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: !['bar', 'horizontalBar'].includes(chartType), // Ocultar leyenda para gráficos de barras
        position: 'top' as const,
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          title: function(context: any) {
            const index = context[0]?.dataIndex;
            if (index !== undefined && data[index]?.fullName) {
              return data[index].fullName;
            }
            return context[0]?.label || '';
          },
          label: function(context: any) {
            const value = context.parsed.y || context.parsed || context.raw;
            return `${title}: ${value}`;
          }
        }
      }
    },
    scales: undefined, // Se define específicamente en cada tipo de gráfico
  };

  const renderChart = () => {
    const chartData = generateChartData();
    
    // Opciones específicas para gráficos con escalas cartesianas
    const cartesianChartOptions = {
      ...chartOptions,
      scales: {
        x: isRatingChart ? {
          title: {
            display: true,
            text: 'Calificación (1-10)'
          }
        } : undefined,
        y: {
          beginAtZero: true,
          ticks: isRatingChart ? {
            stepSize: 1,
            precision: 0
          } : undefined,
          title: {
            display: true,
            text: isRatingChart ? 'Nº de respuestas' : 'Cantidad'
          }
        },
      }
    };
    
    switch (chartType) {
      case 'bar':
        return <Bar data={chartData} options={cartesianChartOptions} />;
      case 'pie':
        return <Pie data={chartData} options={chartOptions} />;
      case 'line':
        return <Line data={chartData} options={cartesianChartOptions} />;
      case 'doughnut':
        return <Doughnut data={chartData} options={chartOptions} />;
      case 'polarArea':
        return <PolarArea data={chartData} options={chartOptions} />;
      case 'radar':
        return <Radar data={chartData} options={chartOptions} />;
      case 'table':
        return renderTable();
      case 'horizontalBar':
        return renderHorizontalBar();
      default:
        return <Bar data={chartData} options={cartesianChartOptions} />;
    }
  };

  const Icon = chartTypeIcons[chartType];

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-primary" />
            <CardTitle className="text-sm font-medium">{title}</CardTitle>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowControls(!showControls)}
          >
            <Settings className="h-4 w-4" />
          </Button>
        </div>
        {description && <CardDescription className="text-xs">{description}</CardDescription>}
        
        {showControls && (
          <div className="flex gap-2 mt-3 flex-wrap">
            <Select value={chartType} onValueChange={(value: ChartType) => setChartType(value)}>
              <SelectTrigger className="h-8 text-xs w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bar">Barras</SelectItem>
                <SelectItem value="horizontalBar">Barras Horizontales</SelectItem>
                <SelectItem value="pie">Circular</SelectItem>
                <SelectItem value="line">Línea</SelectItem>
                <SelectItem value="doughnut">Dona</SelectItem>
                <SelectItem value="polarArea">Polar</SelectItem>
                <SelectItem value="radar">Radar</SelectItem>
                <SelectItem value="table">Tabla</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={colorPalette} onValueChange={(value: keyof typeof colorPalettes) => setColorPalette(value)}>
              <SelectTrigger className="h-8 text-xs w-32">
                <Palette className="h-3 w-3" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Por defecto</SelectItem>
                <SelectItem value="ocean">Océano</SelectItem>
                <SelectItem value="nature">Naturaleza</SelectItem>
                <SelectItem value="sunset">Atardecer</SelectItem>
                <SelectItem value="purple">Púrpura</SelectItem>
                <SelectItem value="monochrome">Monocromático</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        
        <div className="flex justify-between items-center gap-1 mt-2">
          <div className="flex gap-1">
            <Badge variant="secondary" className="text-xs">
              {data.length} elementos
            </Badge>
            <Badge variant="outline" className="text-xs capitalize">
              {chartType}
            </Badge>
          </div>
          {averageValue !== undefined && (
            <Badge variant="default" className="text-xs font-semibold bg-primary text-primary-foreground">
              Promedio: {averageValue.toFixed(1)}/10
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className={chartType === 'table' ? 'max-h-64' : 'h-64'}>
          {renderChart()}
        </div>
      </CardContent>
    </Card>
  );
};